import React from "react";
import {
  Typography,
  Paper,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Tooltip,
  Chip,
  Grid,
  Alert,
} from "@mui/material";
import { UploadFile, Delete } from "@mui/icons-material";
import DrawIcon from "@mui/icons-material/Draw";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { formatKeyWord } from "../../utils/formatRole";
import AppButton from "../AppButton";
import theme from "../../utils/theme";
import {
  documentStatus,
  listOfDocuments,
  userRoles,
} from "../../utils/constant";
import DeleteConfirmationPopup from "../DeleteConfirmationPopup";

const MAX_FILES_PER_DOCUMENT = 5;

// Styles object
const styles = {
  paper: {
    elevation: 0,
    sx: {
      border: `${theme.palette.grey[300]} 1.5px solid`,
      borderRadius: 2,
      overflow: "hidden",
    },
  },
  headerBox: {
    display: "flex",
    alignItems: "center",
    p: 2,
    sx: {
      bgcolor: theme.palette.secondary.main,
      borderRadius: "8px 8px 0 0",
    },
  },
  headerTypography: {
    sx: {
      fontWeight: 700,
      fontSize: 18,
      color: theme.palette.text.light,
      overflow: "hidden",
    },
  },
  contentBox: {
    sx: {
      p: 2,
      bgcolor: theme.palette.background.default,
    },
  },
  table: {
    sx: {
      minWidth: 650,
    },
  },
  tableHeadCell: {
    sx: {
      fontWeight: "bold",
      py: 2,
      bgcolor: theme.palette.grey[100],
    },
  },
  tableCell: {
    sx: {
      py: 1.5,
      minWidth: { xs: "80px", sm: "100px", md: "120px", lg: "160px" },
      maxWidth: { xs: "180px", sm: "230px", md: "250px", lg: "600px" },
    },
  },
  tableRow: {
    sx: {
      "&:last-child td, &:last-child th": {
        border: 0,
      },
    },
  },
  chip: {
    sx: {
      border: `1px solid ${theme.palette.grey[500]}`,
      px: 1,
      py: 0.5,
      bgcolor: theme.palette.info.light,
      color: theme.palette.text.primary,
      // width: "100%",
      minWidth: { xs: "150px", md: "200px", lg: "250px" },
      maxWidth: { xs: "150px", md: "200px", lg: "250px" },
      justifyContent: "space-between",
      "& .MuiChip-label": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
      "& .MuiChip-deleteIcon": {
        color: theme.palette.primary.main,
        ml: 2,
        "&:hover": {
          color: theme.palette.primary.dark,
        },
      },
    },
  },
  checkbox: {
    sx: {
      p: 0,
    },
  },
  errorText: {
    sx: {
      color: "error.main",
      fontSize: "0.75rem",
      mt: 0.5,
    },
  },
  alert: {
    severity: "error",
    sx: {
      mb: 2,
      borderRadius: 1,
      background: theme.palette.error.light,
      color: theme.palette.error.dark,
    },
  },
  loader: {
    display: "flex",
    justifyContent: "center",
    height: "70vh",
    alignItems: "center",
    background: "none",
  },
};

const DocumentUploadForm = ({
  formik,
  handleFileChange,
  handleRemoveFile,
  handleCheckboxChange,
  handleDocumentStatusChange,
  isUploadDisabled,
  deleteModal,
  setDeleteModal,
  file,
  setFile,
  role,
  docStatus,
  handleUrlSubmit,
  allDocSigned,
}) => {
  return (
    <Paper {...styles.paper}>
      <Box {...styles.headerBox}>
        <Typography {...styles.headerTypography}>Documents needed</Typography>
      </Box>
      <Box {...styles.contentBox}>
        <form onSubmit={formik.handleSubmit}>
          {/* Show overall form error */}

          <TableContainer
            component={Paper}
            sx={{ overflowY: "scroll", maxHeight: "55vh" }}
          >
            <Table {...styles.table} aria-label="document upload table">
              <TableHead>
                <TableRow>
                  <TableCell {...styles.tableHeadCell}>Document Name</TableCell>
                  <TableCell {...styles.tableHeadCell}>
                    Uploaded Files
                  </TableCell>
                  {(role === userRoles.Document_Controller ||
                    role === userRoles.Admin) &&
                    (docStatus === documentStatus.Awaiting_Documents ||
                      docStatus === documentStatus.Partially_Uploaded) && (
                      <TableCell {...styles.tableHeadCell}>Action</TableCell>
                    )}
                  <TableCell {...styles.tableHeadCell}>
                    Signature needed by the QC Coordinator
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listOfDocuments.map((docName) => {
                  const documentValue = formik?.values?.documents?.[docName];
                  const hasFiles =
                    documentValue?.documents_uploaded?.length > 0;
                  const isDisabled = isUploadDisabled(docName);
                  const error = formik?.errors?.documents?.[docName];

                  return (
                    <TableRow key={docName} {...styles.tableRow}>
                      <TableCell
                        component="th"
                        scope="row"
                        {...styles.tableCell}
                      >
                        {formatKeyWord(docName)}
                        {error?.documents_uploaded && (
                          <Typography {...styles.errorText}>
                            {typeof error?.documents_uploaded === "string"
                              ? error?.documents_uploaded
                              : error?.documents_uploaded?.message ||
                                "Invalid files"}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell {...styles.tableCell}>
                        <Grid container spacing={2}>
                          {hasFiles ? (
                            documentValue?.documents_uploaded?.map((file) => (
                              <Grid
                                size={{ xs: 12, lg: 6 }}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                                key={file.id}
                              >
                                <Tooltip
                                  title={
                                    file.fileName.length > 20
                                      ? file.fileName
                                      : ""
                                  }
                                  placement="top"
                                >
                                  <Chip
                                    label={file.fileName}
                                    onDelete={
                                      (role === userRoles.Document_Controller ||
                                        role === userRoles.Admin) &&
                                      (docStatus ===
                                        documentStatus.Awaiting_Documents ||
                                        docStatus ===
                                          documentStatus.Partially_Uploaded)
                                        ? () => {
                                            if (file.uploaded_document_id) {
                                              setFile({
                                                ...file,
                                                docName: docName,
                                              });
                                              setDeleteModal(true);
                                            } else {
                                              handleRemoveFile(docName, file);
                                            }
                                          }
                                        : role ===
                                            userRoles.Quality_Controller_Coordinator &&
                                          documentValue.signature_required &&
                                          docStatus ===
                                            documentStatus.Awaiting_E_Signature
                                        ? () => handleUrlSubmit(file)
                                        : undefined
                                    }
                                    onClick={() => {
                                      if (file.uploaded_document_id)
                                        window.open(
                                          file.file,
                                          "_blank",
                                          "noopener,noreferrer"
                                        );
                                    }}
                                    deleteIcon={
                                      (role === userRoles.Document_Controller ||
                                        role === userRoles.Admin) &&
                                      (docStatus ===
                                        documentStatus.Awaiting_Documents ||
                                        docStatus ===
                                          documentStatus.Partially_Uploaded) ? (
                                        <Tooltip title="Delete">
                                          <Delete />
                                        </Tooltip>
                                      ) : role ===
                                          userRoles.Quality_Controller_Coordinator &&
                                        documentValue.signature_required &&
                                        docStatus ===
                                          documentStatus.Awaiting_E_Signature ? (
                                        <Tooltip
                                          title={
                                            file.signed
                                              ? "Re-sign Document"
                                              : "Sign Document"
                                          }
                                        >
                                          <DrawIcon />
                                        </Tooltip>
                                      ) : undefined
                                    }
                                    {...styles.chip}
                                  />
                                </Tooltip>
                                {file.signed && (
                                  <Tooltip title="Already Signed">
                                    <CheckCircleIcon color="success" />
                                  </Tooltip>
                                )}
                              </Grid>
                            ))
                          ) : (
                            <Grid size={{ xs: 12 }}>
                              <Typography variant="body2" color="textSecondary">
                                No files uploaded
                              </Typography>
                            </Grid>
                          )}
                          {isDisabled &&
                            (role === userRoles.Document_Controller ||
                              role === userRoles.Admin) && (
                              <Grid size={{ xs: 12 }}>
                                <Typography variant="caption" color="error">
                                  Maximum {MAX_FILES_PER_DOCUMENT} files reached
                                </Typography>
                              </Grid>
                            )}
                        </Grid>
                      </TableCell>
                      {(role === userRoles.Document_Controller ||
                        role === userRoles.Admin) &&
                        (docStatus === documentStatus.Awaiting_Documents ||
                          docStatus === documentStatus.Partially_Uploaded) && (
                          <TableCell align="left" {...styles.tableCell}>
                            <Tooltip
                              title={
                                isDisabled
                                  ? `Maximum ${MAX_FILES_PER_DOCUMENT} files allowed`
                                  : "Upload files"
                              }
                            >
                              <span>
                                <IconButton
                                  color="primary"
                                  aria-label="upload document"
                                  component="label"
                                  disabled={isDisabled}
                                >
                                  <UploadFile />
                                  <input
                                    type="file"
                                    hidden
                                    multiple
                                    onChange={(e) =>
                                      handleFileChange(e, docName)
                                    }
                                    accept=".pdf"
                                    disabled={isDisabled}
                                  />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      <TableCell align="center" {...styles.tableCell}>
                        {hasFiles && (
                          <Checkbox
                            checked={documentValue.signature_required}
                            onChange={(e) => handleCheckboxChange(e, docName)}
                            {...styles.checkbox}
                            name={`documents.${docName}.signature_required`}
                            disabled={
                              !(
                                (role === userRoles.Document_Controller ||
                                  role === userRoles.Admin) &&
                                (docStatus ===
                                  documentStatus.Awaiting_Documents ||
                                  docStatus ===
                                    documentStatus.Partially_Uploaded)
                              )
                            }
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container mt={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12 }}>
              {formik.errors.documents &&
                typeof formik.errors.documents === "string" && (
                  <Alert {...styles.alert}>{formik.errors.documents}</Alert>
                )}
            </Grid>
            {(((role === userRoles.Document_Controller ||
              role === userRoles.Admin) &&
              (docStatus === documentStatus.Awaiting_Documents ||
                docStatus === documentStatus.Partially_Uploaded)) ||
              (role === userRoles.Quality_Controller_Coordinator &&
                docStatus === documentStatus.Awaiting_E_Signature)) && (
              <Grid container size={{ xs: 12 }}>
                <Grid
                  size={{ xs: 12, md: 6 }}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Checkbox
                    checked={formik.values.docStatus}
                    onChange={(e) => handleDocumentStatusChange(e)}
                    {...styles.checkbox}
                    name={`docStatus`}
                    disabled={
                      ((role === userRoles.Document_Controller ||
                        role === userRoles.Admin) &&
                        (docStatus === documentStatus.All_Uploaded ||
                          docStatus === documentStatus.Upload_Completed ||
                          docStatus === documentStatus.Awaiting_E_Signature ||
                          docStatus === documentStatus.All_Signed)) ||
                      (role === userRoles.Quality_Controller_Coordinator &&
                        (docStatus === documentStatus.Partially_Uploaded ||
                          docStatus === documentStatus.All_Signed ||
                          docStatus === documentStatus.Upload_Completed ||
                          docStatus === documentStatus.All_Uploaded ||
                          docStatus === documentStatus.Awaiting_Documents))
                    }
                  />
                  <Typography
                    variant="body2"
                    color={
                      formik.values.docStatus || allDocSigned
                        ? "textPrimary"
                        : "textSecondary"
                    }
                  >
                    {role === userRoles.Document_Controller
                      ? "Confirm all documents Uploaded - no additional uploads after submission"
                      : "Confirm all documents Signed - no additional signatures after submission"}
                  </Typography>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                  sx={{ textAlign: "right !important" }}
                >
                  <AppButton
                    type="submit"
                    buttonType="primary"
                    label={
                      formik.values.docStatus
                        ? role === userRoles.Document_Controller
                          ? "Submit for E-Signature"
                          : "Submit Signed Documents"
                        : role === userRoles.Quality_Controller_Coordinator
                        ? "Submit Signed Documents"
                        : "Upload Documents"
                    }
                    disabled={
                      !formik.isValid || formik.isSubmitting || !formik.dirty
                    }
                    isLoading={formik.isSubmitting}
                    sx={{ width: "max-content", height: "40px" }}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </form>
      </Box>
      <DeleteConfirmationPopup
        open={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={async () => {
          await handleRemoveFile(file.docName, file);
          setDeleteModal(false);
        }}
        itemName={file?.fileName || ""}
      />
    </Paper>
  );
};

export default DocumentUploadForm;
